import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Close as X, ChevronLeft, ChevronRight, PlayArrow as Play, Pause, RotateLeft as RotateCcw, ThumbUp as ThumbUpIcon, ThumbUpOutlined as ThumbUpOutlinedIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';

const FlashcardsModal = ({ 
  isOpen, 
  onClose, 
  subject, 
  chapter, 
  flashcards = [], 
  isExisting = false,
  onRegenerate = null,
  contentId = null
}) => {
  const { user } = useAuth();
  const [currentFlashcards, setCurrentFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRotate, setAutoRotate] = useState(false);
  const [autoRotateInterval, setAutoRotateInterval] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [updatingLike, setUpdatingLike] = useState(false);
  const [flashcardLikes, setFlashcardLikes] = useState({});
  const [updatingFlashcardLike, setUpdatingFlashcardLike] = useState({});

  useEffect(() => {
    if (isOpen && subject && chapter) {
      console.log('🔍 FlashcardsModal useEffect triggered:', { isOpen, subject, chapter, flashcards, isExisting });
      if (flashcards && flashcards.length > 0) {
        // Use provided flashcards (from existing content or newly generated)
        console.log('🔍 Using provided flashcards:', flashcards);
        setCurrentFlashcards(flashcards);
        setCurrentIndex(0);
        setIsFlipped(false); // Always start with question side showing
        setError(null);
      } else if (!isExisting) {
        // Only fetch flashcards if not provided AND this is not for existing content
        console.log('🔍 Fetching new flashcards...');
        fetchFlashcards();
      } else {
        console.log('🔍 No flashcards provided and isExisting=true, showing empty state');
        setCurrentFlashcards([]);
      }
    }
  }, [isOpen, subject, chapter, flashcards, isExisting]);

  useEffect(() => {
    if (autoRotate) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev < currentFlashcards.length - 1) {
            return prev + 1;
          } else {
            // Stop auto-rotate when reaching the last card
            setAutoRotate(false);
            return prev;
          }
        });
        setIsFlipped(false);
      }, 3000);
      setAutoRotateInterval(interval);
    } else {
      if (autoRotateInterval) {
        clearInterval(autoRotateInterval);
        setAutoRotateInterval(null);
      }
    }

    return () => {
      if (autoRotateInterval) {
        clearInterval(autoRotateInterval);
      }
    };
  }, [autoRotate, currentFlashcards.length]);

  const fetchFlashcards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.generateFlashcards(subject, chapter);
      
      setCurrentFlashcards(response.flashcards || []);
      setCurrentIndex(0);
      setIsFlipped(false);
    } catch (err) {
      console.error('Error fetching flashcards:', err);
      setError('Failed to load flashcards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false); // Reset flip state when navigating
    }
  };

  const handleNext = () => {
    if (currentIndex < currentFlashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false); // Reset flip state when navigating
    }
  };

  const handleFlip = () => {
    console.log('🔍 handleFlip called, current isFlipped:', isFlipped);
    setIsFlipped(!isFlipped);
  };

  const handleAutoRotate = () => {
    setAutoRotate(!autoRotate);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setAutoRotate(false);
  };

  const handleClose = () => {
    setAutoRotate(false);
    setCurrentIndex(0);
    setIsFlipped(false);
    onClose();
  };

  const handleRegenerate = async () => {
    if (onRegenerate) {
      onRegenerate();
    } else {
      await fetchFlashcards();
    }
  };

  const handleLike = async () => {
    if (!contentId || updatingLike) return;
    
    setUpdatingLike(true);
    try {
      await apiService.likeContent(contentId, 'flashcard', !isLiked);
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Error updating like status:', error);
    } finally {
      setUpdatingLike(false);
    }
  };

  const handleFlashcardLike = async (flashcardIndex, liked) => {
    if (updatingFlashcardLike[flashcardIndex]) return;
    
    setUpdatingFlashcardLike(prev => ({ ...prev, [flashcardIndex]: true }));
    try {
      // For now, we'll save the like status locally since we don't have individual flashcard IDs
      setFlashcardLikes(prev => ({ ...prev, [flashcardIndex]: liked }));
      // TODO: Add API endpoint for individual flashcard likes
    } catch (error) {
      console.error('Error updating flashcard like status:', error);
    } finally {
      setUpdatingFlashcardLike(prev => ({ ...prev, [flashcardIndex]: false }));
    }
  };

  // Reset flip state when currentIndex changes
  useEffect(() => {
    setIsFlipped(false);
  }, [currentIndex]);

  // Debug isFlipped state changes
  useEffect(() => {
    console.log('🔍 isFlipped state changed to:', isFlipped);
  }, [isFlipped]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <motion.div 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="bg-gradient-to-r from-teal-500 to-blue-600 p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{chapter} - Flashcards</h2>
                <p className="text-teal-100">{subject}</p>
              </div>
              
              {/* Only X button on top right */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>
          </motion.div>

          {/* Content */}
          <div className="p-6">
            {/* Like Button for Flashcards */}
            {contentId && (
              <div className="flex justify-end mb-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLike}
                  disabled={updatingLike}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isLiked 
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/30'
                  }`}
                >
                  {updatingLike ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isLiked ? (
                    <ThumbUpIcon className="w-4 h-4" />
                  ) : (
                    <ThumbUpOutlinedIcon className="w-4 h-4" />
                  )}
                  {isLiked ? 'Liked' : 'Like'}
                </motion.button>
              </div>
            )}

            {loading ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center h-64"
              >
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Generating flashcards...</p>
                </div>
              </motion.div>
            ) : error ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center h-64 flex items-center justify-center"
              >
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                  <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
              </motion.div>
            ) : currentFlashcards.length > 0 ? (
              <>
                {/* Compact Flashcard */}
                <div className="flex justify-center mb-4">
                  <div className="relative w-full max-w-md">
                    <div 
                      className="relative w-full h-64 cursor-pointer" 
                      onClick={() => {
                        console.log('🔍 Card clicked!');
                        handleFlip();
                      }}
                    >
                      <motion.div
                        initial={{ rotateY: 0 }}
                        animate={{ rotateY: isFlipped ? 180 : 0 }}
                        transition={{ 
                          duration: 0.6, 
                          ease: "easeInOut"
                        }}
                        style={{
                          transformStyle: 'preserve-3d',
                          width: '100%',
                          height: '100%'
                        }}
                        className="relative"
                      >
                        {/* Front - Question */}
                        <motion.div
                          style={{
                            backfaceVisibility: 'hidden',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            transform: 'rotateY(0deg)'
                          }}
                          className="bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 rounded-xl shadow-lg p-6 flex flex-col items-center justify-center text-center border border-blue-200 dark:border-gray-600"
                        >
                          <div className="w-full h-full flex flex-col justify-center">
                            <div className="mb-3">
                              <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full">
                                Q{currentIndex + 1}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 leading-relaxed mb-4">
                              {(() => {
                                const question = currentFlashcards[currentIndex]?.question;
                                console.log('🔍 Rendering question:', question, 'for index:', currentIndex);
                                return question || 'No question available';
                              })()}
                            </h3>
                            <div className="mt-auto">
                              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                👆 Tap to reveal answer
                              </p>
                            </div>
                          </div>
                        </motion.div>
                        
                        {/* Back - Answer */}
                        <motion.div
                          style={{
                            backfaceVisibility: 'hidden',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            transform: 'rotateY(180deg)'
                          }}
                          className="bg-gradient-to-br from-teal-50 via-teal-100 to-green-100 dark:from-gray-700 dark:via-gray-600 dark:to-gray-500 rounded-xl shadow-lg p-6 flex flex-col items-center justify-center text-center border border-teal-200 dark:border-gray-600"
                        >
                          <div className="w-full h-full flex flex-col justify-center">
                            <div className="mb-3">
                              <span className="inline-block px-3 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-semibold rounded-full">
                                A{currentIndex + 1}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 leading-relaxed mb-4">
                              {(() => {
                                const answer = currentFlashcards[currentIndex]?.answer;
                                console.log('🔍 Rendering answer:', answer, 'for index:', currentIndex);
                                return answer || 'No answer available';
                              })()}
                            </h3>
                            <div className="mt-auto">
                              <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">
                                👆 Tap to see question
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    </div>
                    

                  </div>
                </div>

                                {/* Compact Navigation */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="flex items-center justify-center gap-3 mb-4"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </motion.button>
                  
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 rounded-lg shadow-md border border-white dark:border-gray-700">
                    <span className="text-lg font-bold text-white">
                      {currentIndex + 1} / {currentFlashcards.length}
                    </span>
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNext}
                    disabled={currentIndex === currentFlashcards.length - 1}
                    className="p-3 rounded-lg bg-gradient-to-r from-teal-500 to-green-600 text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </motion.button>
                </motion.div>

                {/* Compact Action Controls */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                  className="flex items-center justify-center gap-2 flex-wrap mb-4"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      console.log('🔍 Show Answer button clicked!');
                      handleFlip();
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-teal-500 to-green-600 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
                  >
                    {isFlipped ? 'Question' : 'Answer'}
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAutoRotate}
                    className={`px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium flex items-center gap-1 ${
                      autoRotate 
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
                        : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                    }`}
                  >
                    {autoRotate ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    {autoRotate ? 'Stop' : 'Auto'}
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleReset}
                    className="px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reset
                  </motion.button>

                  {onRegenerate && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleRegenerate}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
                    >
                      New Set
                    </motion.button>
                  )}
                </motion.div>

                {/* Like Box for Entire Flashcard Set */}
                {currentIndex === currentFlashcards.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="flex justify-center"
                  >
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-600">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 text-center">
                        How helpful was this flashcard set?
                      </p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleLike()}
                          disabled={updatingLike}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                            isLiked 
                              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-2 border-green-300 dark:border-green-600'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                          }`}
                        >
                          {updatingLike ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : isLiked ? (
                            <ThumbUpIcon className="w-4 h-4" />
                          ) : (
                            <ThumbUpOutlinedIcon className="w-4 h-4" />
                          )}
                          <span className="font-medium">Helpful</span>
                        </button>
                        
                        <button
                          onClick={() => handleLike()}
                          disabled={updatingLike}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                            !isLiked && isLiked !== null
                              ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-2 border-red-300 dark:border-red-600'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                          }`}
                        >
                          {updatingLike ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : !isLiked && isLiked !== null ? (
                            <ThumbUpOutlinedIcon className="w-4 h-4 transform rotate-180" />
                          ) : (
                            <ThumbUpOutlinedIcon className="w-4 h-4 transform rotate-180" />
                          )}
                          <span className="font-medium">Not Helpful</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </>
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center h-64 flex items-center justify-center"
              >
                <p className="text-gray-600 dark:text-gray-400">No flashcards available</p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FlashcardsModal; 